import { useCallback, useEffect, useRef, useState } from 'react';

import { reportMediaError, reportPlaybackStall, trackEvent } from '@/lib/errorReporting';
import { requestRecovery } from '@/lib/recovery';

const STALL_CHECK_INTERVAL_MS = 2000;
const STALL_THRESHOLD_MS = 8000;
const MAX_STALL_RECOVERY_ATTEMPTS = 3;

// A failed load is retried rather than accepted, because the failure actually
// seen in production is transient. Display 20 reported MEDIA_ERR_NETWORK at
// 04:49 UTC — 06:49 in Oslo, the panel powering on for the day and reaching for
// a video before the network was ready. Seconds later the network is fine, but
// nothing tried again.
//
// Backoff rather than a fixed delay: if the network is still coming up, three
// attempts two seconds apart would all fail for the same reason.
const LOAD_RETRY_DELAYS_MS = [2000, 5000, 15000];

interface VideoBlockProps {
  src: string;
  objectFit?: 'contain' | 'cover';
  rotation?: number;
  pageId?: number;
  boundryId?: string | null;
  isActive?: boolean;
  loop?: boolean;
  onEnded?: () => void;
  /** Retries exhausted — this video is not going to play. */
  onFailed?: () => void;
  /** Frames are really being presented. */
  onPlaying?: () => void;
}

const rotationStyle = {
  [180]: {
    transform: 'rotate(180deg)',
  },
  [90]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(90deg) translateY(-100%)',
    WebkitTransform: 'rotate(90deg) translateY(-100%)',
    MozTransform: 'rotate(90deg) translateY(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
  [270]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(270deg) translateX(-100%)',
    WebkitTransform: 'rotate(270deg) translateX(-100%)',
    MozTransform: 'rotate(270deg) translateX(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
} as const;

function VideoBlock({ src, objectFit, rotation = 0, isActive, loop = true, onEnded, onFailed, onPlaying }: VideoBlockProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Whether this element currently has a picture on screen. Drives whether it
  // covers the page in black or lets the theme colour through — see the style
  // on the <video> below.
  const [isShowingFrame, setIsShowingFrame] = useState(false);
  const playRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastProgressRef = useRef<{ time: number; at: number }>({ time: 0, at: 0 });
  const stallRecoveryAttemptsRef = useRef(0);
  const loadRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadAttemptsRef = useRef(0);

  const clearLoadRetry = () => {
    if (!loadRetryTimeoutRef.current) {
      return;
    }

    clearTimeout(loadRetryTimeoutRef.current);
    loadRetryTimeoutRef.current = null;
  };

  /**
   * A load failed. Try again, and only give up once out of attempts.
   *
   * Previously this path reported and stopped. On a "play the whole video"
   * page that left the playlist waiting for an `ended` event that could never
   * arrive, so one transient network blip at power-on stopped the screen for
   * the rest of the day.
   */
  /**
   * Find out WHY the video could not be fetched.
   *
   * MEDIA_ERR_NETWORK only says the download broke part-way. It does not
   * distinguish a name that would not resolve from a connection that was
   * refused from a server that answered badly — and those need different
   * fixes. So immediately after a failure, ask for the same URL directly and
   * record what happens.
   *
   * `no-cors` because the media host sends no CORS headers, which means the
   * response is opaque and its status cannot be read. That still separates the
   * two cases that matter: a request that RESOLVES reached the server, and one
   * that THROWS never got there at all.
   */
  const diagnoseFetchFailure = (mediaError: MediaError | null) => {
    const startedAt = Date.now();

    const record = (outcome: Record<string, unknown>) => {
      reportPlaybackStall('video fetch diagnosis', {
        src,
        mediaErrorCode: mediaError?.code ?? null,
        online: navigator.onLine,
        tookMs: Date.now() - startedAt,
        ...outcome,
      });
    };

    try {
      // HEAD, emphatically not GET. A GET here would download the entire video
      // a second time, at the exact moment the network is already struggling —
      // turning a diagnostic into a way of making the fault worse.
      fetch(src, { method: 'HEAD', mode: 'no-cors', cache: 'no-store' })
        .then(response => {
          record({
            reachedServer: true,
            // Opaque, so `status` reads 0 and `type` reads "opaque". The fact
            // that it resolved at all is the finding: the host was reachable,
            // so the failure was not connectivity.
            responseType: response.type,
            status: response.status,
          });
        })
        .catch((err: unknown) => {
          const error = err as Error;
          record({
            reachedServer: false,
            errorName: error?.name,
            errorMessage: error?.message,
          });
        });
    } catch {
      // Diagnosis must never affect playback.
    }
  };

  const handleLoadFailure = (video: HTMLVideoElement) => {
    reportMediaError('video', src, video.error);

    // Only on the first failure. The condition that caused it is often gone
    // seconds later, so this has to sample the moment it actually broke.
    if (loadAttemptsRef.current === 0) {
      diagnoseFetchFailure(video.error);
    }

    const delay = LOAD_RETRY_DELAYS_MS[loadAttemptsRef.current];

    if (delay === undefined) {
      trackEvent('video', 'load retries exhausted', { src, attempts: loadAttemptsRef.current });
      // Hand back to PlayList. Moving on shows the rest of the playlist
      // instead of one dead page, and the next cycle round will try this
      // video again from scratch.
      onFailed?.();
      return;
    }

    loadAttemptsRef.current += 1;
    trackEvent('video', 'load retry', { src, attempt: loadAttemptsRef.current, delayMs: delay });

    clearLoadRetry();
    loadRetryTimeoutRef.current = setTimeout(() => {
      loadRetryTimeoutRef.current = null;

      if (!video.isConnected) {
        return;
      }

      try {
        video.load();
      } catch {
        // load() on a torn-down element is not worth reporting; the next
        // activation rebuilds it anyway.
      }
    }, delay);
  };

  const clearPlayRetry = () => {
    if (!playRetryTimeoutRef.current) {
      return;
    }

    clearTimeout(playRetryTimeoutRef.current);
    playRetryTimeoutRef.current = null;
  };

  const clearStallWatch = () => {
    if (!stallIntervalRef.current) {
      return;
    }

    clearInterval(stallIntervalRef.current);
    stallIntervalRef.current = null;
  };

  const prepareForAutoplay = (video: HTMLVideoElement) => {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
  };

  const tryPlay = (video: HTMLVideoElement, retryOnAbort = true) => {
    if (!video.isConnected) {
      return;
    }

    const playResult = video.play();

    if (!playResult || typeof playResult.catch !== 'function') {
      return;
    }

    playResult.catch(err => {
      const isAbort = err?.name === 'AbortError';

      if (isAbort && retryOnAbort) {
        clearPlayRetry();
        playRetryTimeoutRef.current = setTimeout(() => {
          if (!video.isConnected || !video.paused) {
            return;
          }
          tryPlay(video, false);
        }, 80);
        return;
      }

      console.warn('Video playback failed:', err);
    });
  };

  // Release the hardware video decoder. Samsung Tizen has only one decoder,
  // and simply unmounting the element isn't always enough to free it before
  // the next page's video tries to claim it.
  const releaseDecoder = (video: HTMLVideoElement) => {
    try {
      if (!video.paused) {
        video.pause();
      }
      video.removeAttribute('src');
      while (video.firstChild) {
        video.removeChild(video.firstChild);
      }
      video.load();
    } catch (err) {
      console.warn('Video decoder release failed:', err);
    }
  };

  const recoverFromStall = (video: HTMLVideoElement) => {
    if (!video.isConnected) {
      return;
    }

    if (stallRecoveryAttemptsRef.current >= MAX_STALL_RECOVERY_ATTEMPTS) {
      // In-place recovery has failed. Previously this gave up silently, which
      // is why a dead decoder could leave a screen white for weeks with no
      // trace anywhere. Report it, then hand off to the (opt-in) recovery path.
      console.warn('Video stall recovery gave up after repeated attempts.');
      clearStallWatch();

      reportPlaybackStall('recovery attempts exhausted', {
        src,
        currentTime: video.currentTime,
        readyState: video.readyState,
        networkState: video.networkState,
        paused: video.paused,
        errorCode: video.error?.code ?? null,
        attempts: stallRecoveryAttemptsRef.current,
      });

      requestRecovery('video-stall-exhausted', { src }, { report: false });
      return;
    }

    stallRecoveryAttemptsRef.current += 1;
    console.warn(`Video appears stalled, attempting recovery (${stallRecoveryAttemptsRef.current}).`);

    prepareForAutoplay(video);
    try {
      video.load();
    } catch (err) {
      console.warn('Video reload during stall recovery failed:', err);
    }

    lastProgressRef.current = { time: video.currentTime, at: Date.now() };
    tryPlay(video, false);
  };

  const startStallWatch = (video: HTMLVideoElement) => {
    clearStallWatch();
    lastProgressRef.current = { time: video.currentTime, at: Date.now() };
    stallRecoveryAttemptsRef.current = 0;

    stallIntervalRef.current = setInterval(() => {
      if (!video.isConnected) {
        clearStallWatch();
        return;
      }

      const now = Date.now();

      // Ended is a legitimate resting state: with loop={false} the page is
      // waiting for PlayList to advance, so this is not a stall.
      if (video.ended) {
        lastProgressRef.current = { time: video.currentTime, at: now };
        return;
      }

      if (video.currentTime !== lastProgressRef.current.time) {
        lastProgressRef.current = { time: video.currentTime, at: now };
        stallRecoveryAttemptsRef.current = 0;
        return;
      }

      // Paused while it is supposed to be playing. This used to be treated as
      // benign and refreshed the progress marker, so a decoder that died
      // quietly was never detected at all — no warning, no report, nothing.
      // Nudge it, but let the stall clock keep running.
      if (video.paused) {
        prepareForAutoplay(video);
        tryPlay(video, false);
      }

      if (now - lastProgressRef.current.at >= STALL_THRESHOLD_MS) {
        recoverFromStall(video);
      }
    }, STALL_CHECK_INTERVAL_MS);
  };

  /**
   * A <source> failed. Several fire in sequence as the browser walks the
   * candidate list, so only report once the element has exhausted them all —
   * networkState NETWORK_NO_SOURCE (3) means nothing is left to try.
   */
  const handleSourceError = useCallback(() => {
    const video = videoRef.current;

    if (!video || video.networkState !== HTMLMediaElement.NETWORK_NO_SOURCE) {
      return;
    }

    reportMediaError('video', src, video.error);
  }, [src]);

  // Fires exactly when the <video> element mounts (el) or unmounts (null).
  // Using a ref callback (instead of effect cleanup) lets us release the
  // decoder right as React removes the element, without interfering with
  // src updates that reuse the same element.
  const handleVideoRef = useCallback((el: HTMLVideoElement | null) => {
    if (el) {
      videoRef.current = el;
      return;
    }

    const prev = videoRef.current;
    if (!prev) {
      return;
    }

    clearPlayRetry();
    clearLoadRetry();
    clearStallWatch();
    releaseDecoder(prev);
    videoRef.current = null;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    clearPlayRetry();
    clearLoadRetry();
    clearStallWatch();
    // A fresh source, or a fresh activation, gets a fresh set of retries.
    loadAttemptsRef.current = 0;
    // And nothing is on screen yet, so cover the card again until it is. This
    // is what keeps the gap between two videos black rather than flashing the
    // theme colour.
    setIsShowingFrame(false);
    prepareForAutoplay(video);

    if (isActive === false) {
      return;
    }

    video.load();
    startStallWatch(video);
    return () => {
      clearPlayRetry();
      clearLoadRetry();
      clearStallWatch();
    };
  }, [isActive, src]);

  // Only render the <video> element for the active page.
  // Samsung Tizen only supports a single hardware video decoder - multiple
  // <video> elements in the DOM simultaneously cause decoder conflicts.
  if (isActive === false) {
    return <div className="w-full h-full relative" />;
  }

  return (
    <div className="w-full h-full relative">
      <video
        ref={handleVideoRef}
        controls={false}
        autoPlay
        playsInline
        loop={loop}
        preload="auto"
        muted
        onEnded={() => {
          if (!loop) {
            onEnded?.();
          }
        }}
        // A media element that cannot load or decode fires this EVENT — it
        // never throws, so without an explicit handler the failure is
        // completely invisible: no exception, no Sentry event, just a screen
        // showing nothing.
        onError={() => {
          const video = videoRef.current;
          if (video) {
            handleLoadFailure(video);
          }
        }}
        onCanPlay={() => {
          const video = videoRef.current;
          if (!video || !video.paused) {
            return;
          }

          prepareForAutoplay(video);
          tryPlay(video, false);
        }}
        onPlaying={() => {
          trackEvent('video', 'playing', { src });
          // It recovered. Reset so that a failure hours from now gets a full
          // set of retries rather than inheriting attempts already spent.
          loadAttemptsRef.current = 0;
          clearLoadRetry();
          // Frames are on screen, so stop covering the page. From here the
          // letterbox bands show the theme colour somebody chose.
          setIsShowingFrame(true);
          onPlaying?.();
        }}
        className="w-full h-full"
        style={{
          objectFit: objectFit ?? 'contain',
          // Black ONLY while this element is not showing a frame.
          //
          // A transparent video lets the theme's card show through, and on
          // Gjensidige's theme that card is #fff — which is why a clip that
          // failed to load left a white screen. Covering it black is the guard.
          //
          // But once frames are playing, the same transparency is what puts the
          // designer's chosen colour in the letterbox bands of an `objectFit:
          // contain` clip. Holding black there would replace a deliberate colour
          // with black on every page, which is a worse trade than the failure it
          // protects against.
          backgroundColor: isShowingFrame ? 'transparent' : '#000',
          ...(rotation !== 0 ? rotationStyle[rotation.toString() as unknown as keyof typeof rotationStyle] : {}),
        }}
      >
        {/* Sources are <source> children rather than video.src, and per spec a
            failed source-selection fires `error` on the SOURCE elements — the
            <video> element gets no error event at all, only
            networkState === NETWORK_NO_SOURCE. So the handler above would never
            see a 404 video. Catch it here instead, and only report once the
            element has actually run out of candidates. */}
        <source src={src} type="video/mp4" onError={handleSourceError} />
        <source src={src} type="video/webm" onError={handleSourceError} />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

export default VideoBlock;
